import { memo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Flex, Typography } from 'antd';
import cn from 'classnames';

import { type OnboardingStep } from '@/entities/onboarding';

import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';

import cls from './onboarding-step-wrapper.module.css';

interface OnboardingStepWrapperProps {
    step: OnboardingStep;
    className?: string | undefined;
    children: ReactNode;
    onNext?: (() => void) | undefined;
    isLastStep?: boolean | undefined;
    isProcessing?: boolean | undefined;
    nextButtonText?: string | undefined;
    onSubmit?: (() => void) | undefined;
}

export const OnboardingStepWrapper = memo(
    (props: OnboardingStepWrapperProps) => {
        const { t } = useTranslation('onboarding');

        const {
            step,
            children,
            onNext,
            isLastStep,
            className,
            isProcessing,
            nextButtonText,
            onSubmit,
        } = props;

        const handleNext = () => {
            if (isLastStep && onSubmit) {
                onSubmit();
            } else {
                onNext?.();
            }
        };

        const getNextButtonText = () => {
            if (nextButtonText) {
                return nextButtonText;
            }

            return isLastStep ? t('actions.finish') : t('actions.next');
        };

        return (
            <Card
                className={cn(cls.card, className)}
                footerActions={
                    <Flex
                        align="center"
                        justify="flex-end"
                    >
                        <Button
                            loading={isProcessing ?? false}
                            onClick={handleNext}
                            title={getNextButtonText()}
                        />
                    </Flex>
                }
                isMobileFlat
                isWithoutPadding
                title={
                    <>
                        <Typography.Title
                            level={2}
                            style={{ marginBottom: 0 }}
                        >
                            {step.title}
                        </Typography.Title>

                        {step.subtitle && (
                            <Typography.Text style={{ fontSize: 14 }}>
                                {step.subtitle}
                            </Typography.Text>
                        )}
                    </>
                }
            >
                <div className={cls.content}>{children}</div>
            </Card>
        );
    },
);

OnboardingStepWrapper.displayName = 'OnboardingStepWrapper';
